import { supabase } from "@/integrations/supabase/client";
import type { JobIntelUserProfile, JobIntelProfileDocument } from "@/types/jobintelProfile";

// ── Profile CRUD ──

export async function getProfile(userId: string): Promise<JobIntelUserProfile | null> {
  const { data, error } = await supabase
    .from('jobintel_user_profiles' as any)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data as unknown as JobIntelUserProfile | null;
}

export async function upsertProfile(profile: Partial<JobIntelUserProfile> & { user_id: string }): Promise<JobIntelUserProfile> {
  const payload = {
    ...profile,
    profile_completion_percentage: calculateCompletionPercentage(profile as JobIntelUserProfile),
  };
  // Determine if profile_complete based on percentage
  payload.profile_complete = (payload.profile_completion_percentage ?? 0) >= 50;

  const { data, error } = await supabase
    .from('jobintel_user_profiles' as any)
    .upsert(payload as any, { onConflict: 'user_id' })
    .select()
    .single();
  if (error) throw error;
  return data as unknown as JobIntelUserProfile;
}

// ── Documents CRUD ──

export async function insertProfileDocument(doc: Omit<JobIntelProfileDocument, 'id'>): Promise<JobIntelProfileDocument> {
  const { data, error } = await supabase
    .from('jobintel_profile_documents' as any)
    .insert(doc as any)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as JobIntelProfileDocument;
}

export async function updateProfileDocument(id: string, updates: Partial<JobIntelProfileDocument>): Promise<void> {
  const { error } = await supabase
    .from('jobintel_profile_documents' as any)
    .update(updates as any)
    .eq('id', id);
  if (error) throw error;
}

export async function getProfileDocuments(userId: string): Promise<JobIntelProfileDocument[]> {
  const { data, error } = await supabase
    .from('jobintel_profile_documents' as any)
    .select('*')
    .eq('user_id', userId)
    .order('uploaded_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as JobIntelProfileDocument[];
}

// ── Completion Percentage ──

interface FieldWeight { field: string; weight: number; }

const REQUIRED_FIELDS: FieldWeight[] = [
  { field: 'current_job_title', weight: 3 },
  { field: 'target_job_titles', weight: 3 },
  { field: 'current_city', weight: 3 },
  { field: 'current_state', weight: 3 },
  { field: 'current_zip', weight: 3 },
  { field: 'salary_minimum_cents', weight: 3 },
  { field: 'remote_preference', weight: 3 },
  { field: 'years_of_experience', weight: 3 },
];

const IMPORTANT_FIELDS: FieldWeight[] = [
  { field: 'target_industries', weight: 2 },
  { field: 'target_seniority_level', weight: 2 },
  { field: 'employment_type', weight: 2 },
  { field: 'core_skills', weight: 2 },
  { field: 'education_level', weight: 2 },
  { field: 'salary_target_cents', weight: 2 },
  { field: 'preferred_company_sizes', weight: 2 },
  { field: 'availability', weight: 2 },
];

const NICE_FIELDS: string[] = [
  'current_industry', 'experience_level', 'education_field', 'certifications',
  'technical_skills', 'leadership_skills', 'industries_worked_in', 'notable_employers',
  'career_summary', 'resume_text', 'linkedin_url', 'target_job_title_variations',
  'excluded_industries', 'open_to_relocation', 'relocation_cities',
  'max_commute_miles', 'max_commute_minutes', 'hybrid_days_acceptable',
  'willing_to_travel', 'max_travel_percentage', 'open_to_variable_comp',
  'variable_comp_minimum_base_cents', 'interested_in_equity', 'currently_employed',
  'preferred_company_structure', 'target_companies', 'excluded_companies',
  'required_keywords', 'excluded_keywords', 'required_benefits',
  'requires_visa_sponsorship', 'work_schedule_requirements', 'deal_breakers',
];

function isFilled(value: any): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return true;
  return false;
}

export function calculateCompletionPercentage(profile: Partial<JobIntelUserProfile>): number {
  if (!profile) return 0;
  const p = profile as any;
  let totalWeight = 0;
  let filledWeight = 0;

  for (const { field, weight } of [...REQUIRED_FIELDS, ...IMPORTANT_FIELDS]) {
    totalWeight += weight;
    if (isFilled(p[field])) filledWeight += weight;
  }
  for (const field of NICE_FIELDS) {
    totalWeight += 1;
    if (isFilled(p[field])) filledWeight += 1;
  }

  return Math.min(100, Math.round((filledWeight / totalWeight) * 100));
}

// ── Profile Context for Agents ──

export function getProfileContext(profile: JobIntelUserProfile | null): string {
  if (!profile) return 'No professional profile available for this user.';

  const parts: string[] = [];

  if (profile.current_job_title) parts.push(`Current Title: ${profile.current_job_title}`);
  if (profile.years_of_experience) parts.push(`Years of Experience: ${profile.years_of_experience}`);
  if (profile.experience_level) parts.push(`Experience Level: ${profile.experience_level}`);
  if (profile.target_job_titles?.length) parts.push(`Target Roles: ${profile.target_job_titles.join(', ')}`);
  if (profile.target_industries?.length) parts.push(`Target Industries: ${profile.target_industries.join(', ')}`);
  if (profile.current_city && profile.current_state) parts.push(`Location: ${profile.current_city}, ${profile.current_state}`);
  if (profile.remote_preference) parts.push(`Remote Preference: ${profile.remote_preference.replace(/_/g, ' ')}`);
  if (profile.salary_target_cents) parts.push(`Target Salary: $${(profile.salary_target_cents / 100).toLocaleString()}`);
  if (profile.core_skills?.length) parts.push(`Top Skills: ${profile.core_skills.slice(0, 10).join(', ')}`);
  if (profile.education_level) parts.push(`Education: ${profile.education_level.replace(/_/g, ' ')}`);

  return parts.length > 0
    ? `Professional Profile:\n${parts.join('\n')}`
    : 'Profile exists but has minimal data.';
}
